import {
  AgeAssuranceAttemptDependencies,
  AgeAssuranceAttemptTrigger,
  AgeAssuranceGateState,
  AgeAssuranceNativeResult,
  AgeAssuranceOutcome,
  PlatformAgeAssuranceResult,
  UserGeo,
} from '../types';

import { classifyAgeRange, hasCompleteJurisdiction, isTexasJurisdiction } from './decision';

type StartupOptions = AgeAssuranceAttemptDependencies & {
  enabled: boolean;
  isWeb: boolean;
};

type RetryOptions = AgeAssuranceAttemptDependencies & {
  enabled: boolean;
};

type StateListener = (state: AgeAssuranceGateState) => void;

const allowedResult = (
  outcome: AgeAssuranceOutcome,
  nativeResult: AgeAssuranceNativeResult = 'not_called'
) => ({
  state: 'allowed' as const,
  outcome,
  nativeResult,
});

export const executeAgeAssuranceAttempt = async (
  dependencies: AgeAssuranceAttemptDependencies
): Promise<{
  state: Exclude<AgeAssuranceGateState, 'pending'>;
  outcome: AgeAssuranceOutcome;
  nativeResult: AgeAssuranceNativeResult;
}> => {
  let geo: UserGeo | null;

  try {
    geo = await dependencies.queryUserGeo();
  } catch {
    return allowedResult('geography_unavailable');
  }

  if (!hasCompleteJurisdiction(geo)) {
    return allowedResult('geography_unavailable');
  }

  if (!isTexasJurisdiction(geo)) {
    return allowedResult('outside_texas');
  }

  let platformResult: PlatformAgeAssuranceResult;
  try {
    platformResult = await dependencies.requestPlatformAgeAssurance();
  } catch {
    return allowedResult('native_failure', 'error_unknown');
  }

  if (platformResult.outcome !== 'age_range') {
    return allowedResult(platformResult.outcome, platformResult.nativeResult);
  }

  const classification = classifyAgeRange(platformResult);
  if (classification === 'under_13') {
    return {
      state: 'blocked',
      outcome: 'age_under_13',
      nativeResult: 'response_under_threshold',
    };
  }

  if (classification === 'age_13_plus') {
    return allowedResult('age_13_plus', 'response_at_or_above_threshold');
  }

  return allowedResult(
    'native_unavailable',
    classification === 'invalid' ? 'response_invalid' : 'response_ambiguous'
  );
};

export class AgeAssuranceCoordinator {
  private state: AgeAssuranceGateState = 'pending';
  private inFlight: Promise<AgeAssuranceGateState> | null = null;
  private listeners = new Set<StateListener>();

  getState = (): AgeAssuranceGateState => this.state;

  subscribe = (listener: StateListener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  start = (options: StartupOptions): Promise<AgeAssuranceGateState> => {
    if (this.state !== 'pending' || this.inFlight) {
      return this.inFlight ?? Promise.resolve(this.state);
    }

    if (options.isWeb) {
      return this.completeBypass(options, 'web_bypass', 'startup');
    }

    if (!options.enabled) {
      return this.completeBypass(options, 'flag_disabled', 'startup');
    }

    return this.runAttempt(options, 'startup');
  };

  retry = (options: RetryOptions): Promise<AgeAssuranceGateState> => {
    if (this.inFlight) {
      return this.inFlight;
    }

    if (this.state !== 'blocked') {
      return Promise.resolve(this.state);
    }

    this.setState('pending');
    if (!options.enabled) {
      return this.completeBypass(options, 'flag_disabled', 'retry');
    }

    return this.runAttempt(options, 'retry');
  };

  resetForTesting = (): void => {
    this.state = 'pending';
    this.inFlight = null;
    this.listeners.clear();
  };

  private completeBypass = (
    dependencies: AgeAssuranceAttemptDependencies,
    outcome: Extract<AgeAssuranceOutcome, 'flag_disabled' | 'web_bypass'>,
    trigger: AgeAssuranceAttemptTrigger
  ): Promise<AgeAssuranceGateState> => {
    dependencies.logTerminalDecision({
      message: 'Age assurance decision completed',
      ageAssuranceOutcome: outcome,
      ageAssuranceTrigger: trigger,
      ageAssuranceNativeResult: 'not_called',
    });
    this.setState('allowed');
    return Promise.resolve('allowed');
  };

  private runAttempt = (
    dependencies: AgeAssuranceAttemptDependencies,
    trigger: AgeAssuranceAttemptTrigger
  ): Promise<AgeAssuranceGateState> => {
    const attempt = executeAgeAssuranceAttempt(dependencies)
      .then(result => {
        dependencies.logTerminalDecision({
          message: 'Age assurance decision completed',
          ageAssuranceOutcome: result.outcome,
          ageAssuranceTrigger: trigger,
          ageAssuranceNativeResult: result.nativeResult,
        });
        this.setState(result.state);
        return result.state;
      })
      .finally(() => {
        if (this.inFlight === attempt) {
          this.inFlight = null;
        }
      });

    this.inFlight = attempt;
    return attempt;
  };

  private setState = (state: AgeAssuranceGateState): void => {
    this.state = state;
    this.listeners.forEach(listener => listener(state));
  };
}

export const processAgeAssuranceCoordinator = new AgeAssuranceCoordinator();
